import ActionPerformer from "@/utils/moreMenu/ActionPerformer.ts";
import {ActionPlaceholder} from "@/utils/moreMenu/ActionHandler.ts";
import ActionPerformerUtil from "@/utils/moreMenu/ActionPerformerUtil.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import ListViewPerformerHelper from "@/utils/moreMenu/ListViewPerformerHelper.ts";
import CustomReportLauncherAction from "@/utils/editView/editScreens/CustomReportLauncherAction.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import {Dialog} from "quasar";
import BulkEditDialog from "@/components/EditView/BulkEditDialog.vue";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import Translator from "@/utils/common/Translator.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import DTOBulkEditField = namaTypes.DTOBulkEditField;
import EntityReferenceData = namaTypes.EntityReferenceData;

export default class ListViewPerformer extends ActionPerformer {
  private presenter: ListPresenter;
  private readonly helper: ListViewPerformerHelper;

  constructor(presenter: ListPresenter) {
    super();
    this.presenter = presenter;
    this.helper = new ListViewPerformerHelper(presenter);
  }

  async calcDefaultsOf(action: ActionPlaceholder, callback: (fields: namaTypes.DTOFilledQuestionField[]) => void) {
    await ActionPerformerUtil.calcDefaultsOf(action, callback, this.helper);
  }

  getSearchContextProvider(): ListViewPerformerHelper {
    return this.helper;
  }

  isRecordNotSaved(): boolean {
    return false;
  }

  async performAction(action: ActionPlaceholder, questionFields: namaTypes.DTOFilledQuestionField[]) {
    await ActionPerformerUtil.performAction(action, questionFields, this.presenter.fetchMetadata(), this.helper);
  }

  async runCustomAction(action: ActionPlaceholder) {
    let selectedRows: EntityReferenceData[];
    if (ObjectChecker.isNotEmptyOrNull(this.helper.externalContextFields)) {
      const rowNode = this.presenter.gridApi.value?.getRowNode(this.helper.externalContextFields.namaRowIndex);
      selectedRows = [rowNode.data]
    } else
      selectedRows = AGGridUtils.fetchSelectedRows(this.presenter.gridApi.value!)
    if (!selectedRows || !selectedRows.length) {
      useErrorsStore().pushErrorStr("pleaseSelectRows");
      return;
    }
    if (action.customization && action.customization.reportInfo && action.customization.reportInfo.reportId) {
      let menuItem = new CustomReportLauncherAction(this.presenter, action.customization.reportInfo);
      if (menuItem.executeFn)
        menuItem.executeFn();
      return;
    }
    let selectedRowIds: EntityReferenceData[];
    const listLayout = this.presenter.layout;
    const runCustomActionOn = action.customization.runCustomActionOn || listLayout.objectName;
    if (runCustomActionOn)
      selectedRowIds = selectedRows.map(row => {
        if (runCustomActionOn in row)
          return {
            entityType: row[runCustomActionOn][CommonFieldIds.ENTITY_TYPE],
            id: row[runCustomActionOn][CommonFieldIds.ID]
          }
        else
          return null;
      }).filter((item: EntityReferenceData, index, array) => {
        if (ObjectChecker.isEmptyOrNull(item))
          return false;
        return index == array.findIndex(v => v.id == item.id)
      });
    else
      selectedRowIds = selectedRows.map(l => ({
        entityType: l[CommonFieldIds.ENTITY_TYPE],
        id: l[CommonFieldIds.ID]
      }));
    let req: namaTypes.PerformActionReq = {
      actionInfo: action.customization,
      targetRecords: selectedRowIds
    }
    let result = await NamaCommon.namaFetch(NamaURLS.PERFORM_Custom_action, {data: req});
    if (result && result.data)
      await this.presenter.onCustomActionSuccess(result.data);
  }

  fetchLastTerminalPayTrnx(): Promise<void> {
    return Promise.resolve(undefined);
  }

  payInvoice(paymentValue: number): Promise<void> {
    return Promise.resolve(undefined);
  }

  readFirstWeight(): Promise<void> {
    return Promise.resolve(undefined);
  }

  readSecondWeight(): Promise<void> {
    return Promise.resolve(undefined);
  }

  signDocuments(onlySelected: boolean): Promise<void> {
    return Promise.resolve(undefined);
  }

  override async bulkEdit(action: ActionPlaceholder): Promise<void> {
    let externalContextFields = this.helper.externalContextFields;
    let targetRowIds: string[];
    if (ObjectChecker.isNotEmptyOrNull(externalContextFields.namaRowIndex))
      targetRowIds = [externalContextFields.id]
    else
      targetRowIds = AGGridUtils.fetchSelectedRows(this.helper.gridApi).map(l => l.id);
    if (ObjectChecker.isEmptyOrNull(targetRowIds)) {
      useErrorsStore().pushErrorStr(Translator.translateId("pleaseSelectRows"));
      return;
    }
    const settings: NamaAxiosRequestConfig = {
      params: {
        bulkEditId: action.customization?.bulkEditId
      },
    }
    const data: any = await NamaCommon.namaFetch(NamaURLS.FETCH_BULK_EDIT_FIELDS, settings)
    const bulkEditFields = data?.list as DTOBulkEditField[];
    const editPlace = new EditPlace({
      [PlaceTokens.PRM_ENTITY_TYPE]: this.presenter.entityType,
      [PlaceTokens.PRM_ID]: targetRowIds[0]
    });
    Dialog.create({
      component: BulkEditDialog,
      componentProps: {
        editPlace,
        bulkEditFields,
        selectedRowIds: targetRowIds,
        action
      }
    });
  }

  showCurrentApprovalCase(): Promise<void> {
    return Promise.resolve(undefined);
  }
}
